import { useContext, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import clsx from 'clsx';
import * as R from 'ramda';

import { Snackbar } from '@atom/components/common/mui/snackbar/Snackbar';
import WorkUsers from '@atom/components/common/workUsers/WorkUsers';
import WorkOrderContext, {
  WorkOrderActionTypes,
} from '@atom/components/workOrderDetail/WorkOrderContext';
import { TASK_UPDATE } from '@atom/graph/task';
import { useWorkValidations } from '@atom/hooks/useWorkValidations';
import { Icon, IconButton, Modal } from '@atom/mui';
import colors from '@atom/styles/colors';
import { Task, TaskUpdateInput } from '@atom/types/task';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import {
  isCurrentTenant,
  Tenant,
} from '@atom/utilities/featureToggleUtilities';
import timeUtilities from '@atom/utilities/timeUtilities';
import { getValidationsFromError } from '@atom/utilities/workValidationUtilities';

import classes from './workOrderTasks.module.css';

interface Props {
  task: Task;
}

const TaskListItem = ({ task }: Props) => {
  const {
    workOrderDetail,
    task: activeTask,
    setActiveTask,
    dispatch,
    refetch,
  } = useContext(WorkOrderContext);

  const { workValidations, setWorkValidations } = useWorkValidations();

  const [completeModalOpen, setCompleteModalOpen] = useState<boolean>(false);

  const [taskUpdate, { loading: taskUpdateLoading }] = useMutation<
    { taskUpdate: Task },
    { input: TaskUpdateInput }
  >(TASK_UPDATE);

  const setTask = (updatedTask: Task) => {
    dispatch({
      type: WorkOrderActionTypes.SET_TASK,
      data: {
        ...updatedTask,
      },
    });
  };

  const handleCompleteToggle = async () => {
    try {
      const newCompleteValue = !task.isCompleted;
      const completeText = newCompleteValue ? 'Complete' : 'Incomplete';

      Snackbar.info({ message: `Marking ${task.name} ${completeText}...` });

      const res = await taskUpdate({
        variables: {
          input: {
            id: task.id,
            workOrderId: workOrderDetail.id,
            isCompleted: newCompleteValue,
          },
        },
      });

      setTask(res?.data?.taskUpdate);

      Snackbar.info({ message: `Marked ${task.name} ${completeText}.` });

      refetch();
    } catch (err) {
      if (err.networkError.statusCode === 422) {
        const taskValidations = getValidationsFromError(err);

        setWorkValidations({
          workOrderId: workOrderDetail.id,
          taskValidations,
        });
      }

      const message =
        err.networkError.statusCode === 422
          ? 'Unable to mark complete. Task required fields are missing.'
          : 'Something went wrong. Please try again or contact administrator.';

      Snackbar.error({ message });
    } finally {
      setCompleteModalOpen(false);
    }
  };

  const completeIcon = task.isCompleted
    ? 'check_circle'
    : 'check_circle_outline';
  const completeColor = task.isCompleted
    ? colors.brand.green
    : colors.neutral.silver;
  const tooltipText = task.isCompleted ? 'Mark Incomplete' : 'Mark Complete';

  const handleCompleteClick = () => {
    return task.isCompleted
      ? handleCompleteToggle()
      : setCompleteModalOpen(true);
  };

  // TODO: [AM-7948]: Move to RBAC
  const disableTaskCompleteButton =
    doesNotHaveRolePermissions(
      isCurrentTenant([Tenant.GWRR]) ? ROLE_SETS.MANAGER : ROLE_SETS.INSPECTOR,
    ) || workOrderDetail.isClosed;

  const hasPendingChanges = useMemo(() => {
    return task.assetIds.some(
      id => workOrderDetail.assets[id]?.hasPendingChanges,
    );
  }, [task]);

  const isTaskInvalid = R.has(task.id)(workValidations?.taskValidations);

  return (
    <>
      <div
        className={clsx(
          classes.taskListItemContainer,
          activeTask && task.id === activeTask.id && classes.selected,
        )}
        onClick={() => setActiveTask(task.id)}
      >
        {isTaskInvalid ? (
          <Icon color={colors.brand.red}>error_outline</Icon>
        ) : (
          <div className={classes.changeIndicatorContainer}>
            <IconButton
              size="small"
              onClick={handleCompleteClick}
              disabled={disableTaskCompleteButton}
              tooltip={tooltipText}
            >
              <Icon color={completeColor}>{completeIcon}</Icon>
            </IconButton>
            {hasPendingChanges && <div className={classes.changeIndicator} />}
          </div>
        )}
        <div className={classes.taskListContentContainer}>
          <div className={classes.taskTitle}>{task.name}</div>
          {R.isEmpty(task.users) && R.isEmpty(task.userGroups) ? (
            <div className={classes.noAssigneesMessage}>No Assignees</div>
          ) : (
            <WorkUsers
              users={task.users as any}
              userGroups={task.userGroups as any}
              avatarSize={32}
              showTaskUserStatus
            />
          )}
          {task?.completionDate && (
            <div
              className={classes.taskSubtext}
            >{`Completed on ${timeUtilities.setDisplayDate(
              task?.completionDate,
            )} by ${task?.completedByName}`}</div>
          )}
        </div>
      </div>
      <Modal
        open={completeModalOpen}
        loading={taskUpdateLoading}
        confirmButtonText="Complete"
        onCancel={() => setCompleteModalOpen(false)}
        onConfirm={handleCompleteToggle}
        title="Complete Task?"
      >
        Not everyone completed the task. This action will complete the task for
        everyone on the task. Are you sure?
      </Modal>
    </>
  );
};

export default TaskListItem;
